// Notificações no celular: o cartão de permissão do Início (depois da 2ª partida terminada; "Agora não" volta em 7
// dias), a seção de Configurações com uma chave por tipo e o app instalável (Chrome e Android pelo botão; iPhone pelo
// Compartilhar > Adicionar à Tela de Início).
import { OFENSIVA_POR_DIA, PUSH_POR_DIA, SILENCIO_DO_PUSH } from '@truco6/planos';
import { useEffect, useState } from 'react';
import { ErroDaApi } from './api.ts';
import { avisarNaTela } from './aviso.tsx';
import type { Sessao } from './conta.ts';
import { lerRetrospecto } from './Retrospecto.tsx';
import {
  type EstadoDoPush, PERGUNTA, TEXTO_DO_TIPO, TIPOS_DE_PUSH, type TipoPush, agoraNao, aoMudarInstalar, ativarPush, chaveDoServidor, definirPreferencia,
  desativarPush, ehIphone, estadoDoPush, instalado, instalar, lerPreferencias, mostrarCartao, podeInstalar,
} from './push.ts';

const BLOQUEADO = 'As notificações estão bloqueadas neste navegador. Para liberar, toque no cadeado ao lado do endereço ou abra as configurações do aparelho.';

function IconeCompartilhar() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Compartilhar" role="img">
      <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M6 11v9h12v-9" />
    </svg>
  );
}

function IconeAdicionar() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Adicionar" role="img">
      <rect x="4" y="4" width="16" height="16" rx="4" /><path d="M12 8v8M8 12h8" />
    </svg>
  );
}

// No iPhone, o aviso só chega com o jogo na tela de início (iOS 16.4 ou mais novo). As figuras são as do Safari.
export function PassosDoIphone() {
  return (
    <ol className="passos-iphone">
      <li>No Safari, toque em Compartilhar <IconeCompartilhar />, na barra de baixo.</li>
      <li>Toque em <b>Adicionar à Tela de Início</b> <IconeAdicionar /> e depois em <b>Adicionar</b>.</li>
      <li>Abra o Truco de 6 pelo ícone novo e ative as notificações por lá.</li>
    </ol>
  );
}

// Início: a pergunta, com Quero receber e Agora não. Some com o push ativo, bloqueado ou sem as chaves no servidor.
export function CartaoPush({ sessao }: { sessao: Sessao }) {
  const [visivel, setVisivel] = useState(false);
  const [estado, setEstado] = useState<EstadoDoPush | null>(null);
  const [passos, setPassos] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    let vivo = true;
    (async () => {
      const [e, k] = await Promise.all([estadoDoPush(), chaveDoServidor(sessao)]);
      if (!k || !mostrarCartao(Infinity, e)) return;
      const r = await lerRetrospecto(sessao, sessao.id).catch(() => ({ partidas: [] }));
      if (vivo && mostrarCartao(r.partidas.length, e)) { setEstado(e); setVisivel(true); }
    })().catch(() => {});
    return () => { vivo = false; };
  }, [sessao.id]);
  if (!visivel || !estado) return null;
  async function quero() {
    if (estado === 'instalar_iphone') { setPassos(true); return; }
    setOcupado(true);
    try {
      const e = await ativarPush(sessao, 'cartao');
      if (e === 'ativo') { avisarNaTela('Pronto. Os avisos chegam neste aparelho.'); setVisivel(false); }
      else if (e === 'bloqueado') { avisarNaTela(BLOQUEADO); setVisivel(false); }
    } catch (err) { avisarNaTela(err instanceof ErroDaApi || err instanceof Error ? err.message : 'Não deu certo. Tente de novo.'); } finally { setOcupado(false); }
  }
  return (
    <section className="cartao cartao-push" aria-label="Notificações fora do jogo">
      <div className="cab-push">
        <span className="sino-push" aria-hidden="true">🔔</span>
        <p><b>{PERGUNTA}</b></p>
      </div>
      <small className="nota">Chamado para a mesa, pedido de amizade, ofensiva e liga. No máximo {PUSH_POR_DIA} por dia, e nada entre {SILENCIO_DO_PUSH.de}h00 e {SILENCIO_DO_PUSH.ate}h00, a não ser o chamado.</small>
      {passos && <PassosDoIphone />}
      <div className="dupla">
        <button className="btn2" onClick={() => { agoraNao(); setVisivel(false); }}>Agora não</button>
        {!passos && <button className="btn2 dourado" disabled={ocupado} onClick={() => void quero()}>Quero receber</button>}
      </div>
    </section>
  );
}

// Configurações: ativar ou desativar neste aparelho e a chave de cada tipo (valem para a conta, em todo aparelho).
// children: o que mais a seção tem fora do push (a chave dos avisos pelo WhatsApp, em Configurações).
export function SecaoPush({ sessao, children }: { sessao: Sessao; children?: React.ReactNode }) {
  const [estado, setEstado] = useState<EstadoDoPush | null>(null);
  const [ligadoNoServidor, setLigadoNoServidor] = useState<boolean | null>(null);
  const [tipos, setTipos] = useState<Record<TipoPush, boolean> | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    void estadoDoPush().then((e) => { if (vivo) setEstado(e); });
    void chaveDoServidor(sessao).then((k) => { if (vivo) setLigadoNoServidor(!!k); });
    lerPreferencias(sessao).then((r) => { if (vivo) setTipos(r.tipos); }, () => {});
    return () => { vivo = false; };
  }, [sessao.id]);
  async function ativar() {
    setOcupado('aparelho');
    try {
      const e = await ativarPush(sessao, 'configuracoes');
      setEstado(e);
      if (e === 'ativo') avisarNaTela('Pronto. Os avisos chegam neste aparelho.');
    } catch (err) { avisarNaTela(err instanceof Error ? err.message : 'Não deu certo. Tente de novo.'); } finally { setOcupado(null); }
  }
  async function desativar() {
    setOcupado('aparelho');
    try { await desativarPush(sessao); setEstado(await estadoDoPush()); avisarNaTela('Este aparelho não recebe mais os avisos.'); } finally { setOcupado(null); }
  }
  async function mudar(t: TipoPush, ligado: boolean) {
    setOcupado(t);
    setTipos((x) => (x ? { ...x, [t]: ligado } : x));
    try { setTipos((await definirPreferencia(sessao, t, ligado)).tipos); } catch (err) {
      setTipos((x) => (x ? { ...x, [t]: !ligado } : x));
      avisarNaTela(err instanceof ErroDaApi ? err.message : 'Não deu certo. Tente de novo.');
    } finally { setOcupado(null); }
  }
  if (!estado || ligadoNoServidor === null) return null;
  return (
    <section className="secao-amigos secao-push" aria-label="Notificações">
      <h2 className="rotulo">NOTIFICAÇÕES</h2>
      {!ligadoNoServidor ? <p className="cartao nota">As notificações fora do jogo estão desligadas por enquanto.</p> : (
        <>
          <div className="cartao aparelho-push">
            {estado === 'ativo' && (<><p><b>Ativadas neste aparelho.</b></p><button className="btn2" disabled={ocupado === 'aparelho'} onClick={() => void desativar()}>Desativar neste aparelho</button></>)}
            {estado === 'inativo' && (<><p>Este aparelho ainda não recebe os avisos.</p><button className="btn2 dourado" disabled={ocupado === 'aparelho'} onClick={() => void ativar()}>Ativar neste aparelho</button></>)}
            {estado === 'bloqueado' && <p>{BLOQUEADO}</p>}
            {estado === 'instalar_iphone' && (<><p>No iPhone, os avisos chegam com o Truco de 6 na tela de início:</p><PassosDoIphone /></>)}
            {estado === 'sem_suporte' && <p>Este navegador não recebe notificações. No Android, use o Chrome; no iPhone, ponha o jogo na tela de início.</p>}
          </div>
          {tipos && (
            <ul className="cartao tipos-push">
              {TIPOS_DE_PUSH.map((t) => (
                <li key={t}>
                  <span className="texto-chave"><b id={'push-' + t}>{TEXTO_DO_TIPO[t].nome}</b><small>{TEXTO_DO_TIPO[t].quando}</small></span>
                  <button role="switch" aria-checked={tipos[t]} aria-labelledby={'push-' + t} className={'chave-whats' + (tipos[t] ? ' on' : '')}
                    disabled={ocupado === t} onClick={() => void mudar(t, !tipos[t])}><span className="bolinha" /></button>
                </li>
              ))}
            </ul>
          )}
          <p className="nota">No máximo {PUSH_POR_DIA} avisos por dia, e {OFENSIVA_POR_DIA} da ofensiva. Entre {SILENCIO_DO_PUSH.de}h00 e {SILENCIO_DO_PUSH.ate}h00, só o chamado para a mesa chega. Nunca mandamos aviso de compra, oferta ou Tentos.</p>
        </>
      )}
      {children}
    </section>
  );
}

// O jogo na tela de início: o botão do navegador (Chrome, Edge, Android) ou os passos do iPhone.
export function SecaoInstalar() {
  const [pode, setPode] = useState(podeInstalar());
  useEffect(() => aoMudarInstalar(() => setPode(podeInstalar())), []);
  const ja = instalado();
  if (!ja && !pode && !ehIphone()) return null;
  return (
    <section className="secao-amigos" aria-label="Instalar o app">
      <h2 className="rotulo">APP NA TELA DE INÍCIO</h2>
      <div className="cartao aparelho-push">
        {ja ? <p>O Truco de 6 já está na tela de início deste aparelho.</p> : pode ? (
          <>
            <p>Ponha o Truco de 6 na tela de início e abra a mesa com um toque, sem a barra do navegador.</p>
            <button className="btn2 dourado" onClick={() => void instalar()}>Instalar o Truco de 6</button>
          </>
        ) : (<><p>Ponha o Truco de 6 na tela de início do iPhone:</p><PassosDoIphone /></>)}
      </div>
    </section>
  );
}
